import { useEffect, useState } from 'react';
import {
  PreviewBranchReceipt,
  PreviewBusinessNameSMS,
  PreviewReceiptOnboard,
  PreviewWhatsappBranch,
  PreviewWhatsappOnboard
} from '@images';

import { STORAGE_KEYS } from '@constants/storage';
import {
  BusinessNameSampleFlow,
  MixpanelEventV2,
  sendMixpanelEventV2
} from '@core/analytics/mixpanel';
import { useAppSelector } from '@core/store';
import ModalV2 from '@layouts/modal/Modal.v2';
import { getLocalStorageItem } from '@utils/storage';

interface PreviewModalProps {
  onClose: () => void;
  flow: 'orgOnboard' | 'branch' | 'individualOnboard';
  analyticsFlow: BusinessNameSampleFlow;
  profileName?: string;
}

const RENDER_PREVIEW_CONTENT_CLASS = 'flex h-full items-center justify-center';

const PreviewModal = ({
  onClose,
  flow,
  analyticsFlow,
  profileName
}: PreviewModalProps) => {
  const [stepper, setStepper] = useState<number>(1);
  const currentUser = useAppSelector((state) => state.user.currentUser) as {
    mobile?: string;
    name?: string;
  };

  useEffect(() => {
    sendMixpanelEventV2(MixpanelEventV2.BUSINESS_NAME_SAMPLE_VIEWED, {
      mobile_number:
        currentUser?.mobile || getLocalStorageItem(STORAGE_KEYS.MOBILE) || '',
      country_code: '91',
      profile_name: profileName || currentUser?.name || '',
      screen_count: stepper,
      flow: analyticsFlow
    });
  }, [
    stepper,
    analyticsFlow,
    profileName,
    currentUser?.mobile,
    currentUser?.name
  ]);

  const handleDismiss = () => {
    sendMixpanelEventV2(MixpanelEventV2.BUSINESS_NAME_SAMPLE_DISMISSED, {
      mobile_number:
        currentUser?.mobile || getLocalStorageItem(STORAGE_KEYS.MOBILE) || '',
      country_code: '91',
      profile_name: profileName || currentUser?.name || '',
      screen_count: stepper,
      flow: analyticsFlow
    });
    onClose();
  };

  const renderFirstPreview = () => {
    let content;
    switch (flow) {
      case 'orgOnboard':
        content = <PreviewWhatsappOnboard />;
        break;
      case 'branch':
        content = <PreviewWhatsappBranch />;
        break;
      case 'individualOnboard':
        content = <PreviewWhatsappOnboard />;
        break;
      default:
        content = null;
    }

    return <div className={RENDER_PREVIEW_CONTENT_CLASS}>{content}</div>;
  };
  const renderSecondPreview = () => {
    let content;
    switch (flow) {
      case 'orgOnboard':
        content = <PreviewReceiptOnboard />;
        break;
      case 'branch':
        content = <PreviewBranchReceipt />;
        break;
      case 'individualOnboard':
        content = <PreviewReceiptOnboard />;
        break;
      default:
        content = null;
    }

    return <div className={RENDER_PREVIEW_CONTENT_CLASS}>{content}</div>;
  };

  const renderThirdPreview = () => {
    const content = (() => {
      switch (flow) {
        case 'orgOnboard':
        case 'branch':
        case 'individualOnboard':
          return <PreviewBusinessNameSMS />;
        default:
          return null;
      }
    })();

    return <div className={RENDER_PREVIEW_CONTENT_CLASS}>{content}</div>;
  };

  const getButtonProps = () => {
    const buttonConfig = {
      1: {
        primaryButtonLabel: 'Next',
        primaryButtonClick: () => setStepper(2)
      },
      2: {
        primaryButtonLabel: 'Next',
        primaryButtonClick: () => setStepper(3),
        secondaryButtonLabel: 'Back',
        secondaryButtonClick: () => setStepper(1)
      },
      3: {
        primaryButtonLabel: 'Got It',
        primaryButtonClick: handleDismiss,
        secondaryButtonLabel: 'Back',
        secondaryButtonClick: () => setStepper(2)
      }
    };

    return buttonConfig[stepper as keyof typeof buttonConfig] || {};
  };

  const buttonProps = getButtonProps();

  const renderStepperDots = () =>
    [1, 2, 3].map((step) => (
      <div
        key={step}
        className={`size-1.5 rounded-full ${
          stepper === step ? 'bg-primary' : 'bg-grey'
        }`}
      />
    ));

  return (
    <ModalV2
      title="Preview"
      onClose={handleDismiss}
      className="!h-[601px] !w-120 bg-white"
      bodyClassName="overflow-hidden !px-0 flex flex-col"
      isClickOutsideRequired
      primaryButtonStyle="!w-[85px] !h-11"
      secondaryButtonStyle="!h-11 !w-[85px]"
      {...buttonProps}
    >
      <div className="flex w-full flex-1 justify-center overflow-hidden">
        {stepper === 1 && renderFirstPreview()}
        {stepper === 2 && renderSecondPreview()}
        {stepper === 3 && renderThirdPreview()}
      </div>
      <div className="mt-2.5 flex w-full justify-center gap-2">
        {renderStepperDots()}
      </div>
    </ModalV2>
  );
};

export default PreviewModal;
